import { useState } from 'react'

import classNames from 'classnames'
import { Crown, MapPin, Users } from 'lucide-react'
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form'

import { TOURNAMENT_SIZE_LIST } from 'components/atomic/TournamentSizeDropdown/TournamentSizeDropdown'

import type { GroupPlayUiConfig } from '../payload'
import type { CreateTournamentInput, DraftSettings, RegionSettings, TeamFormation } from '../types'
import { ChipGroup, ChoiceCards, FieldError, Help, NumberStepper, Section, SwitchRow, inputClass } from '../ui'
import { BRACKET_OPTIONS, capacityLine, teamSizeError, teamSizeField, teamSizeLimits } from '../wizard'

import { GroupStageControls } from './GroupStageControls'

const TEAM_SIZE_PRESETS = ['1', '2', '3', '4', '5', '6']

export type FormatStepProps = {
  form: UseFormReturn<CreateTournamentInput>
  regionTeamsEnabled: boolean
  teamFormation: TeamFormation
  onTeamFormationChange: (next: TeamFormation) => void
  draftSettings: DraftSettings
  setDraftSettings: (next: DraftSettings) => void
  regionSettings: RegionSettings
  setRegionSettings: (next: RegionSettings) => void
  isGroupPlayEnabled: boolean
  setIsGroupPlayEnabled: (next: boolean) => void
  groupPlayConfig: GroupPlayUiConfig
  setGroupPlayConfig: (next: GroupPlayUiConfig) => void
}

export const FormatStep = ({
  form,
  regionTeamsEnabled,
  teamFormation,
  onTeamFormationChange,
  draftSettings,
  setDraftSettings,
  regionSettings,
  setRegionSettings,
  isGroupPlayEnabled,
  setIsGroupPlayEnabled,
  groupPlayConfig,
  setGroupPlayConfig,
}: FormatStepProps) => {
  const {
    control,
    formState: { errors },
  } = form
  const sizeField = teamSizeField(teamFormation)
  const [numberOfTeams, teamSize, playFormat] = useWatch({ control, name: ['number_of_teams', sizeField, 'play_format'] })
  const solo = teamFormation !== 'teams'

  const formationOptions = [
    {
      value: 'teams' as const,
      title: 'Teams register',
      description: 'Existing teams sign up together with their roster.',
      meta: 'Best for organized teams and leagues',
      icon: <Users size={16} />,
    },
    {
      value: 'captain_pick' as const,
      title: 'Captain draft',
      description: 'Players sign up solo. Captains pick their teams before the bracket starts.',
      meta: 'Great for pickup and community nights',
      icon: <Crown size={16} />,
    },
    ...(regionTeamsEnabled
      ? [
          {
            value: 'region' as const,
            title: 'Region teams',
            description: 'Players sign up solo with their state. You build regional teams; small states merge with neighbors.',
            meta: 'State vs state events',
            icon: <MapPin size={16} />,
          },
        ]
      : []),
  ]

  const bracketHelp = BRACKET_OPTIONS.find((b) => b.id === playFormat)?.help

  return (
    <div>
      <Section title="How are teams made?" description="This decides how players sign up. You can’t change it once people have joined.">
        <ChoiceCards
          label="How are teams made?"
          options={formationOptions}
          value={teamFormation}
          onChange={onTeamFormationChange}
          columns={formationOptions.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}
        />
      </Section>

      {/* Contextual settings for the selected formation */}
      {teamFormation === 'captain_pick' && (
        <Section title="Draft settings">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <ChipGroup
                label="Captains"
                options={[
                  { value: 'random', label: 'Random' },
                  { value: 'manual', label: 'I’ll choose' },
                ]}
                value={draftSettings.captain_selection_method}
                onChange={(v) => setDraftSettings({ ...draftSettings, captain_selection_method: v })}
              />
              <Help>
                {draftSettings.captain_selection_method === 'random'
                  ? 'Captains are picked at random from the players who signed up.'
                  : 'You pick the captains yourself before the draft starts.'}
              </Help>
            </div>
            <div>
              <ChipGroup
                label="Pick order"
                options={[
                  { value: 'snake', label: 'Snake' },
                  { value: 'linear', label: 'Linear' },
                ]}
                value={draftSettings.draft_order_type}
                onChange={(v) => setDraftSettings({ ...draftSettings, draft_order_type: v })}
              />
              <Help>
                {draftSettings.draft_order_type === 'snake'
                  ? 'Order reverses each round (1-2-3, 3-2-1) so last pick gets the next first pick.'
                  : 'Same order every round (1-2-3, 1-2-3).'}
              </Help>
            </div>
          </div>
        </Section>
      )}

      {teamFormation === 'region' && (
        <Section title="Region settings">
          <div className="grid gap-5">
            <div>
              <p className="mb-1.5 text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">Subs per team</p>
              <NumberStepper
                id="ct-region-subs"
                label="Subs per team"
                value={regionSettings.max_subs_per_team}
                min={0}
                max={5}
                onChange={(n) => setRegionSettings({ ...regionSettings, max_subs_per_team: n })}
                suffix={regionSettings.max_subs_per_team === 1 ? 'sub' : 'subs'}
              />
              <Help>Extra players kept on each team’s bench to cover no-shows.</Help>
            </div>
            <SwitchRow
              id="ct-region-balance"
              checked={regionSettings.balance_by_mmr}
              onChange={(v) => setRegionSettings({ ...regionSettings, balance_by_mmr: v })}
              label="Balance teams by skill"
              description="Uses player rating to even out merged regions."
            />
            <SwitchRow
              id="ct-region-intl"
              checked={regionSettings.allow_international}
              onChange={(v) => setRegionSettings({ ...regionSettings, allow_international: v })}
              label="Allow players outside the US"
              description="International players join the closest region."
            />
          </div>
        </Section>
      )}

      <Section title="Bracket" description={bracketHelp}>
        <div data-field="play_format">
          <Controller
            control={control}
            name="play_format"
            rules={{ required: 'Pick a bracket type.' }}
            render={({ field }) => (
              <ChipGroup
                label="Bracket type"
                hideLabel
                options={BRACKET_OPTIONS.map((b) => ({ value: b.id, label: b.label }))}
                value={field.value as (typeof BRACKET_OPTIONS)[number]['id']}
                onChange={field.onChange}
                invalid={!!errors.play_format}
              />
            )}
          />
          <FieldError message={errors.play_format?.message} />
        </div>
      </Section>

      <Section title="Size">
        <div className="grid gap-5">
          {!solo && (
            <div data-field="number_of_teams">
              <Controller
                control={control}
                name="number_of_teams"
                rules={{ required: 'Pick how many teams can join.' }}
                render={({ field }) => (
                  <ChipGroup
                    label={isGroupPlayEnabled ? 'Teams' : 'Bracket size'}
                    options={TOURNAMENT_SIZE_LIST.map((n) => ({ value: n, label: n }))}
                    value={field.value}
                    onChange={field.onChange}
                    invalid={!!errors.number_of_teams}
                  />
                )}
              />
              {errors.number_of_teams ? (
                <FieldError message={errors.number_of_teams.message} />
              ) : (
                <Help>
                  {isGroupPlayEnabled
                    ? 'Teams that can sign up. They’re split into the groups below; signups close when it’s full.'
                    : 'Teams that can sign up. Signups close when it’s full.'}
                </Help>
              )}
            </div>
          )}

          <div data-field={sizeField}>
            <Controller
              key={sizeField}
              control={control}
              name={sizeField}
              rules={{
                required: 'Pick a team size.',
                validate: (v) => teamSizeError(teamFormation, v) ?? true,
              }}
              render={({ field }) => (
                <TeamSizePicker
                  value={String(field.value ?? '')}
                  onChange={field.onChange}
                  invalid={!!errors[sizeField]}
                  min={teamSizeLimits(teamFormation).min}
                  max={teamSizeLimits(teamFormation).max}
                />
              )}
            />
            {errors[sizeField] ? (
              <FieldError message={errors[sizeField]?.message as string} />
            ) : (
              <Help>
                {solo
                  ? `Players per team (${teamSizeLimits(teamFormation).min}–${teamSizeLimits(teamFormation).max}) once teams are formed. Extra signups become subs.`
                  : 'Players on each team’s starting roster.'}
              </Help>
            )}
          </div>

          <p
            className="flex items-center gap-2 rounded-lg bg-btn-fancy-primary-end/[0.06] px-3 py-2 font-display text-base font-semibold text-light-typo-primary dark:text-dark-typo-primary"
            aria-live="polite"
          >
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-green" />
            {capacityLine(teamFormation, numberOfTeams, teamSize)}
          </p>
        </div>
      </Section>

      <Section>
        <SwitchRow
          id="ct-group-play"
          checked={isGroupPlayEnabled}
          onChange={setIsGroupPlayEnabled}
          label="Group stage first"
          description={
            teamFormation === 'captain_pick'
              ? 'Captains draft their teams, then the drafted teams play round-robin groups before the bracket. Needs at least 4 teams’ worth of signups to start.'
              : 'Teams play round-robin groups (best of 1), then the top teams of each group move on to the bracket.'
          }
        />
        {isGroupPlayEnabled && (
          <div className="mt-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary p-4">
            <GroupStageControls
              config={groupPlayConfig}
              setConfig={setGroupPlayConfig}
              teamFormation={teamFormation}
              numberOfTeams={numberOfTeams}
              playFormat={playFormat}
            />
          </div>
        )}
      </Section>
    </div>
  )
}

const TeamSizePicker = ({
  value,
  onChange,
  invalid,
  min,
  max,
}: {
  value: string
  onChange: (v: string) => void
  invalid?: boolean
  min: number
  max: number
}) => {
  const isPreset = TEAM_SIZE_PRESETS.includes(value)
  const [custom, setCustom] = useState(!isPreset && !!value)

  return (
    <div>
      <ChipGroup
        label="Team size"
        options={[
          ...TEAM_SIZE_PRESETS.map((n) => ({
            value: n,
            label: `${n}v${n}`,
            disabled: Number(n) < min || Number(n) > max,
            hint: Number(n) < min || Number(n) > max ? `Needs ${min}–${max} players per team` : undefined,
          })),
          { value: 'custom', label: 'Other' },
        ]}
        value={custom ? 'custom' : isPreset ? value : ''}
        onChange={(v) => {
          if (v === 'custom') {
            setCustom(true)
            if (isPreset) onChange('')
            requestAnimationFrame(() => document.getElementById('ct-team-size-custom')?.focus())
          } else {
            setCustom(false)
            onChange(v)
          }
        }}
        invalid={invalid}
      />
      {custom && (
        <div className="mt-2 flex items-center gap-2">
          <label htmlFor="ct-team-size-custom" className="sr-only">
            Players per team
          </label>
          <input
            id="ct-team-size-custom"
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            placeholder="7"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-invalid={invalid}
            className={classNames(inputClass(invalid), 'w-24 text-center font-display text-lg font-bold tabular-nums')}
          />
          <span className="text-xs text-light-typo-gray dark:text-dark-typo-gray">players per team ({min}–{max})</span>
        </div>
      )}
    </div>
  )
}
